/**
 * Roster admin — SPEC §9.3, §9.4, §4.2, §4.5, §11.6.
 *
 * Two lists, because they fail in different ways. An instructor edit is reversible and
 * affects one person. A location edit reaches into everybody homed there, and §4.2 exists
 * precisely because the tempting version of it — flip `isActive` — silently orphans standing
 * patterns.
 *
 * ## Coordinators see everything and change nothing
 *
 * §9.4 gives them view, chase, and enter-on-behalf. The write controls are not rendered
 * rather than rendered-and-rejected: a button that always 403s is a worse way to learn the
 * rule than not having the button. Clearing a lockout is the one exception, and it is
 * deliberate — §11.6 path 3 is the reason a Coordinator can act at all.
 *
 * ## Destructive things say what they will do, in words
 *
 * Deactivating signs somebody out on the spot, and changing an address invalidates their
 * trusted devices (§11.7). Both are invisible from this screen, so both are stated before
 * the click rather than discovered when an instructor calls to say the app logged them out.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { formatDate, parseISO } from '@gearup/shared';
import { OnBehalf } from './OnBehalf.js';
import {
  ApiError,
  rosterApi,
  type AdminIdentity,
  type InstructorInput,
  type LocationBlocker,
  type LocationInput,
  type RosterInstructorRow,
  type RosterLocationRow,
} from '../../lib/api.js';

type Section = 'instructors' | 'locations';

/**
 * Offered as a list rather than typed, because the server can only tell a typo from a real
 * zone, not a wrong one from a right one: `America/New_York` on a Dallas site validates
 * perfectly and moves that site's deadline by an hour.
 */
const TIMEZONES = [
  { value: 'America/New_York', label: 'Eastern (New York)' },
  { value: 'America/Chicago', label: 'Central (Chicago)' },
  { value: 'America/Denver', label: 'Mountain (Denver)' },
  { value: 'America/Phoenix', label: 'Arizona (no DST)' },
  { value: 'America/Los_Angeles', label: 'Pacific (Los Angeles)' },
  { value: 'America/Anchorage', label: 'Alaska' },
  { value: 'Pacific/Honolulu', label: 'Hawaii' },
];

const BLOCKER_REASON: Record<LocationBlocker['reason'], string> = {
  pattern: 'teaches a standing class here',
  home: 'is homed here',
  assigned: 'is assigned here',
};

export function Roster({
  admin,
  onViewHistory,
}: {
  admin: AdminIdentity;
  /** SPEC §9.3 lists "view history" as a roster row action. It opens Activity, filtered. */
  onViewHistory?: (row: RosterInstructorRow) => void;
}) {
  const canWrite = admin.role === 'ADMIN';

  /** Set while entering somebody's availability for them (SPEC §9.3). */
  const [enteringFor, setEnteringFor] = useState<RosterInstructorRow | null>(null);

  const [section, setSection] = useState<Section>('instructors');
  const [instructors, setInstructors] = useState<RosterInstructorRow[]>([]);
  const [locations, setLocations] = useState<RosterLocationRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const load = useCallback(async () => {
    const data = await rosterApi.list();
    setInstructors(data.instructors);
    setLocations(data.locations);
  }, []);

  useEffect(() => {
    setLoading(true);
    void load()
      .catch(() => setError('Could not load the roster.'))
      .finally(() => setLoading(false));
  }, [load]);

  /** One place to run a write: it reloads, reports, and turns a refusal into a sentence. */
  const run = useCallback(
    async (what: string, action: () => Promise<unknown>) => {
      setError(null);
      setNotice(null);
      try {
        await action();
        await load();
        setNotice(what);
        return true;
      } catch (err) {
        setError(err instanceof ApiError ? err.message : 'That did not work.');
        return false;
      }
    },
    [load],
  );

  if (loading) return <p className="muted center">Loading…</p>;

  // Takes over the whole tab rather than opening inside a row: this is a month of somebody
  // else's availability, and it needs the same room the instructor's own screen gets.
  if (enteringFor) {
    return (
      <OnBehalf
        instructor={enteringFor}
        onDone={() => {
          setEnteringFor(null);
          void load();
        }}
      />
    );
  }

  return (
    <div className="stack">
      <div className="row row--between row--wrap">
        <div className="months" role="tablist" aria-label="Roster sections">
          <button
            role="tab"
            className="months__tab"
            aria-current={section === 'instructors'}
            onClick={() => setSection('instructors')}
          >
            Instructors ({instructors.filter((i) => i.isActive).length})
          </button>
          <button
            role="tab"
            className="months__tab"
            aria-current={section === 'locations'}
            onClick={() => setSection('locations')}
          >
            Locations ({locations.filter((l) => l.isActive).length})
          </button>
        </div>
      </div>

      {!canWrite && (
        <p className="small muted" style={{ margin: 0 }}>
          Coordinators can view the roster and clear a lockout. Roster changes need an Admin
          account.
        </p>
      )}

      {error && (
        <div className="notice notice--warn" role="alert">
          <span className="notice__icon" aria-hidden="true">!</span>
          <span className="grow">{error}</span>
        </div>
      )}
      {notice && (
        <div className="notice notice--success" role="status">
          <span className="notice__icon" aria-hidden="true">✓</span>
          <span className="grow">
            {notice}{' '}
            <button
              className="btn btn--ghost"
              style={{ padding: 0, minHeight: 'auto' }}
              onClick={() => setNotice(null)}
            >
              Dismiss
            </button>
          </span>
        </div>
      )}

      {section === 'instructors' ? (
        <Instructors
          rows={instructors}
          locations={locations}
          canWrite={canWrite}
          run={run}
          onEnterFor={setEnteringFor}
          {...(onViewHistory ? { onViewHistory } : {})}
        />
      ) : (
        <Locations rows={locations} canWrite={canWrite} run={run} />
      )}
    </div>
  );
}

type Run = (what: string, action: () => Promise<unknown>) => Promise<boolean>;

// ---------------------------------------------------------------------------
// Instructors
// ---------------------------------------------------------------------------

function Instructors({
  rows,
  locations,
  canWrite,
  run,
  onEnterFor,
  onViewHistory,
}: {
  rows: RosterInstructorRow[];
  locations: RosterLocationRow[];
  canWrite: boolean;
  run: Run;
  onEnterFor: (row: RosterInstructorRow) => void;
  onViewHistory?: (row: RosterInstructorRow) => void;
}) {
  const [query, setQuery] = useState('');
  const [showInactive, setShowInactive] = useState(false);
  const [editing, setEditing] = useState<string | 'new' | null>(null);
  const [confirmingOff, setConfirmingOff] = useState<string | null>(null);
  const [pausing, setPausing] = useState<string | null>(null);
  const [from, setFrom] = useState('');
  const [until, setUntil] = useState('');

  const shown = useMemo(() => {
    const q = query.trim().toLowerCase();
    return rows.filter((r) => {
      if (!showInactive && !r.isActive) return false;
      if (!q) return true;
      return (
        r.name.toLowerCase().includes(q) ||
        r.email.toLowerCase().includes(q) ||
        (r.homeLocationName ?? '').toLowerCase().includes(q)
      );
    });
  }, [rows, query, showInactive, ]);

  const activeLocations = useMemo(() => locations.filter((l) => l.isActive), [locations]);

  return (
    <div className="card stack">
      <div className="row row--between row--wrap">
        <label className="field grow" style={{ minWidth: 200 }}>
          <span className="field__label">Search</span>
          <input
            className="input"
            value={query}
            placeholder="Name, address, or location"
            onChange={(e) => setQuery(e.target.value)}
          />
        </label>
        <label className="row small" style={{ gap: 6, alignItems: 'center' }}>
          <input
            type="checkbox"
            checked={showInactive}
            onChange={(e) => setShowInactive(e.target.checked)}
          />
          {/* Not a footnote: "did we already have a record for her?" is the question that
              stops a duplicate being created. */}
          Show people who have left
        </label>
        {canWrite && editing !== 'new' && (
          <button className="btn btn--secondary" onClick={() => setEditing('new')}>
            Add instructor
          </button>
        )}
      </div>

      {editing === 'new' && (
        <InstructorForm
          locations={activeLocations}
          onCancel={() => setEditing(null)}
          onSave={async (input) => {
            const ok = await run(`${input.firstName} ${input.lastName} added.`, () =>
              rosterApi.createInstructor(input),
            );
            if (ok) setEditing(null);
          }}
        />
      )}

      {shown.length === 0 ? (
        <p className="muted" style={{ margin: 0 }}>Nobody matches that.</p>
      ) : (
        <ul className="daylist">
          {shown.map((r) => (
            <li key={r.id} className="daylist__row" style={{ flexWrap: 'wrap' }}>
              <span className="daylist__date grow">
                {r.name}
                {!r.isActive && <span className="badge badge--none" style={{ marginLeft: 8 }}>Left</span>}
                {r.lockedOut && (
                  <span className="badge badge--partial" style={{ marginLeft: 8 }}>
                    Locked out · {r.lockoutMinutesRemaining}m
                  </span>
                )}
                {/* ON_LEAVE is an answer, not silence (SPEC §3.7). Without this the row
                    reads as somebody who has gone quiet, and the natural response to that
                    is the reminder leave exists to suppress. */}
                {r.leaves.map((l) => (
                  <span key={l.id} className="badge badge--complete" style={{ marginLeft: 8 }}>
                    {l.active
                      ? `On a break until ${formatDate(parseISO(l.endDate))}`
                      : `Away ${formatDate(parseISO(l.startDate))} to ${formatDate(parseISO(l.endDate))}`}
                  </span>
                ))}
                <br />
                <span className="daylist__meta">
                  {r.email}
                  {r.homeLocationName ? ` · ${r.homeLocationName}` : ' · no home location'}
                  {r.additionalLocations.length > 0 &&
                    ` · also ${r.additionalLocations.map((a) => a.name).join(', ')}`}
                  {r.patternSlots > 0
                    ? ` · ${r.patternSlots} standing class${r.patternSlots === 1 ? '' : 'es'}`
                    : ' · no usual schedule'}
                  {r.enrollwareDisplayName && ` · Enrollware "${r.enrollwareDisplayName}"`}
                  {!r.onboardedAt && r.isActive && ' · never signed in'}
                </span>
              </span>

              <span className="row" style={{ gap: 6 }}>
                {/* Both roles. §11.6 path 3 is the whole reason a Coordinator can act. */}
                {r.lockedOut && (
                  <button
                    className="btn btn--secondary"
                    onClick={() => run(`${r.name}'s lockout cleared.`, () => rosterApi.clearLockout(r.id))}
                  >
                    Clear lockout
                  </button>
                )}
                {/* Both roles: §9.4 makes chasing the Coordinator's job, and recording a
                    break is a chase decision — refusing it would leave them chasing
                    somebody they have just been told is away. */}
                {/* Both roles: §9.4 lists entering availability on somebody's behalf as a
                    Coordinator capability, alongside chasing. */}
                {r.isActive && (
                  <button className="btn btn--ghost" onClick={() => onEnterFor(r)}>
                    Enter availability
                  </button>
                )}
                {/* §9.3's "view history" row action. Offered for inactive people too: the
                    most useful moment for it is after somebody has been deactivated and
                    the question is what happened to their account. */}
                {onViewHistory && (
                  <button className="btn btn--ghost" onClick={() => onViewHistory(r)}>
                    History
                  </button>
                )}
                {/* One button per break, because ending "the" break is ambiguous once
                    there are two, and always offering "Record a break" — a second one is
                    allowed as long as the dates do not overlap. */}
                {r.isActive &&
                  r.leaves.map((l) => (
                    <button
                      key={l.id}
                      className="btn btn--ghost"
                      onClick={() =>
                        run(`${r.name}'s break ended.`, () => rosterApi.leave.end(r.id, l.id))
                      }
                    >
                      {l.active ? 'End break' : `Cancel ${formatDate(parseISO(l.startDate))}`}
                    </button>
                  ))}
                {r.isActive && (
                  <button className="btn btn--ghost" onClick={() => setPausing(r.id)}>
                    Record a break
                  </button>
                )}
                {canWrite && (
                  <>
                    <button className="btn btn--ghost" onClick={() => setEditing(r.id)}>
                      Edit
                    </button>
                    {r.isActive ? (
                      <button className="btn btn--ghost" onClick={() => setConfirmingOff(r.id)}>
                        Deactivate
                      </button>
                    ) : (
                      <button
                        className="btn btn--ghost"
                        onClick={() =>
                          run(`${r.name} is back on the roster.`, () =>
                            rosterApi.setInstructorActive(r.id, true),
                          )
                        }
                      >
                        Reactivate
                      </button>
                    )}
                  </>
                )}
              </span>

              {pausing === r.id && (
                <div className="notice notice--info" style={{ width: '100%', alignItems: 'flex-start' }}>
                  <span className="notice__icon" aria-hidden="true">?</span>
                  <span className="grow">
                    When is <strong>{r.name}</strong> away?
                    <br />
                    <span className="small muted">
                      {/* The distinction that matters, said where the choice is made. */}
                      They keep their access and their history, and it ends by itself. Those
                      days stop counting as cover, and reminders stop for any month the break
                      covers entirely.
                    </span>
                    <br />
                    <span className="row" style={{ marginTop: 8, flexWrap: 'wrap', alignItems: 'flex-end' }}>
                      {/* Both dates, because a break that can only start today cannot say
                          "out for three weeks in November" — which is most of them. Blank
                          start means today. */}
                      <label className="field" style={{ maxWidth: 170 }}>
                        <span className="field__label">From (blank = today)</span>
                        <input
                          className="input"
                          type="date"
                          max={until || undefined}
                          value={from}
                          onChange={(e) => setFrom(e.target.value)}
                        />
                      </label>
                      <label className="field" style={{ maxWidth: 170 }}>
                        <span className="field__label">Until back</span>
                        <input
                          className="input"
                          type="date"
                          min={from || undefined}
                          value={until}
                          onChange={(e) => setUntil(e.target.value)}
                        />
                      </label>
                      <button
                        className="btn btn--primary"
                        disabled={!until}
                        onClick={async () => {
                          const ok = await run(`${r.name} is on a break.`, () =>
                            rosterApi.leave.start(r.id, {
                              ...(from ? { startDate: from } : {}),
                              endDate: until,
                            }),
                          );
                          if (ok) {
                            setPausing(null);
                            setFrom('');
                            setUntil('');
                          }
                        }}
                      >
                        Record it
                      </button>
                      <button
                        className="btn btn--secondary"
                        onClick={() => {
                          setPausing(null);
                          setFrom('');
                          setUntil('');
                        }}
                      >
                        Cancel
                      </button>
                    </span>
                  </span>
                </div>
              )}

              {confirmingOff === r.id && (
                <div className="notice notice--info" style={{ width: '100%', alignItems: 'flex-start' }}>
                  <span className="notice__icon" aria-hidden="true">?</span>
                  <span className="grow">
                    Deactivate <strong>{r.name}</strong>?
                    <br />
                    {/* Stated because it is invisible from here and immediate (§11.7). */}
                    <span className="small muted">
                      They are signed out straight away and stop receiving reminders. Their
                      past availability is kept, and you can bring them back at any time.
                      {/* The two sit next to each other and mean very different things.
                          Deactivating somebody who is coming back in March logs them out and
                          gives no signal on the day they return (SPEC §3.7). */}
                      {' '}If they are coming back, record a break instead.
                    </span>
                    <br />
                    <span className="row" style={{ marginTop: 8 }}>
                      <button
                        className="btn btn--primary"
                        onClick={async () => {
                          await run(`${r.name} deactivated.`, () =>
                            rosterApi.setInstructorActive(r.id, false),
                          );
                          setConfirmingOff(null);
                        }}
                      >
                        Deactivate
                      </button>
                      <button className="btn btn--secondary" onClick={() => setConfirmingOff(null)}>
                        Cancel
                      </button>
                    </span>
                  </span>
                </div>
              )}

              {editing === r.id && (
                <div style={{ width: '100%' }}>
                  <InstructorForm
                    existing={r}
                    locations={activeLocations}
                    onCancel={() => setEditing(null)}
                    onSave={async (input) => {
                      const ok = await run(`${r.name} updated.`, () =>
                        rosterApi.updateInstructor(r.id, input),
                      );
                      if (ok) setEditing(null);
                    }}
                  />
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function InstructorForm({
  existing,
  locations,
  onSave,
  onCancel,
}: {
  existing?: RosterInstructorRow;
  locations: RosterLocationRow[];
  onSave: (input: InstructorInput) => Promise<void>;
  onCancel: () => void;
}) {
  const [firstName, setFirstName] = useState(existing?.firstName ?? '');
  const [lastName, setLastName] = useState(existing?.lastName ?? '');
  const [email, setEmail] = useState(existing?.email ?? '');
  const [ahaInstructorId, setAha] = useState(existing?.ahaInstructorId ?? '');
  const [homeLocationId, setHome] = useState<number | null>(existing?.homeLocationId ?? null);
  const [additional, setAdditional] = useState<number[]>(
    existing?.additionalLocations.map((a) => a.locationId) ?? [],
  );
  const [displayName, setDisplayName] = useState(existing?.enrollwareDisplayName ?? '');
  const [saving, setSaving] = useState(false);

  const emailChanged = Boolean(existing) && email.trim().toLowerCase() !== existing?.email;

  return (
    <form
      className="stack"
      style={{ marginTop: 12 }}
      onSubmit={async (e) => {
        e.preventDefault();
        setSaving(true);
        await onSave({
          firstName,
          lastName,
          email,
          ahaInstructorId: ahaInstructorId.trim() || null,
          homeLocationId,
          additionalLocationIds: additional,
          enrollwareDisplayName: displayName.trim() || null,
        });
        setSaving(false);
      }}
    >
      <div className="row row--wrap" style={{ gap: 'var(--space-2)' }}>
        <label className="field grow">
          <span className="field__label">First name</span>
          <input className="input" value={firstName} onChange={(e) => setFirstName(e.target.value)} required />
        </label>
        <label className="field grow">
          <span className="field__label">Last name</span>
          <input className="input" value={lastName} onChange={(e) => setLastName(e.target.value)} required />
        </label>
      </div>

      <label className="field">
        <span className="field__label">Email</span>
        <input
          className="input"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
        />
      </label>

      {/* §11.7, and §11.6's social-engineering note. The consequence is invisible from here. */}
      {emailChanged && (
        <p className="small" style={{ margin: 0 }}>
          Changing the address signs them out of every remembered device. They will need a
          fresh sign-in link at the new address.
        </p>
      )}

      <div className="row row--wrap" style={{ gap: 'var(--space-2)' }}>
        <label className="field grow">
          <span className="field__label">Home location</span>
          <select
            className="select"
            value={homeLocationId ?? ''}
            onChange={(e) => setHome(e.target.value ? Number(e.target.value) : null)}
          >
            <option value="">Not set</option>
            {locations.map((l) => (
              <option key={l.id} value={l.id}>{l.canonicalName}</option>
            ))}
          </select>
        </label>
        <label className="field grow">
          <span className="field__label">AHA instructor ID</span>
          <input className="input" value={ahaInstructorId} onChange={(e) => setAha(e.target.value)} />
        </label>
      </div>

      <fieldset className="stack" style={{ border: 0, padding: 0, margin: 0 }}>
        <span className="field__label">Also teaches at</span>
        <div className="row row--wrap" style={{ gap: 8 }}>
          {locations
            .filter((l) => l.id !== homeLocationId)
            .map((l) => (
              <label key={l.id} className="row small" style={{ gap: 4, alignItems: 'center' }}>
                <input
                  type="checkbox"
                  checked={additional.includes(l.id)}
                  onChange={(e) =>
                    setAdditional((current) =>
                      e.target.checked ? [...current, l.id] : current.filter((id) => id !== l.id),
                    )
                  }
                />
                {l.canonicalName}
              </label>
            ))}
        </div>
      </fieldset>

      <label className="field">
        <span className="field__label">Enrollware display name</span>
        <input
          className="input"
          value={displayName}
          placeholder="J. Lukose"
          onChange={(e) => setDisplayName(e.target.value)}
        />
        {/* §4.5: three of these are real name divergences, not typos, and the matcher has no
            way to resolve them without being told. */}
        <span className="small muted">
          Only needed when Enrollware shows them under a different name. Must be unique among
          active instructors.
        </span>
      </label>

      <div className="row">
        <button className="btn btn--primary" type="submit" disabled={saving}>
          {saving ? 'Saving…' : existing ? 'Save changes' : 'Add instructor'}
        </button>
        <button className="btn btn--secondary" type="button" onClick={onCancel} disabled={saving}>
          Cancel
        </button>
      </div>
    </form>
  );
}

// ---------------------------------------------------------------------------
// Locations
// ---------------------------------------------------------------------------

function Locations({
  rows,
  canWrite,
  run,
}: {
  rows: RosterLocationRow[];
  canWrite: boolean;
  run: Run;
}) {
  const [retiring, setRetiring] = useState<number | null>(null);
  const [blockers, setBlockers] = useState<LocationBlocker[] | null>(null);
  const [moveTo, setMoveTo] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState<number | 'new' | null>(null);

  const active = useMemo(() => rows.filter((l) => l.isActive), [rows]);

  async function openRetire(id: number) {
    setRetiring(id);
    setBlockers(null);
    setMoveTo(null);
    // Asked BEFORE the button is offered, so §4.2's "list the affected instructors and
    // require each pattern to be reassigned" is a workflow rather than an error message.
    const found = await rosterApi.blockers(id).catch(() => ({ blockers: [] }));
    setBlockers(found.blockers);
  }

  return (
    <div className="card stack">
      {canWrite && (
        <div className="row row--between row--wrap">
          <span className="small muted">
            {/* Luke, 2026-08-23: adding a site is an admin job. Instructors still tell us
                they have started somewhere new; they do not create it themselves. */}
            Instructors cannot add sites. When one tells you about a new one, add it here.
          </span>
          {editing !== 'new' && (
            <button className="btn btn--secondary" onClick={() => setEditing('new')}>
              Add location
            </button>
          )}
        </div>
      )}

      {editing === 'new' && (
        <LocationForm
          onCancel={() => setEditing(null)}
          onSave={async (input) => {
            const ok = await run(`${input.canonicalName} added.`, () =>
              rosterApi.createLocation(input),
            );
            if (ok) setEditing(null);
          }}
        />
      )}

      {rows.length === 0 ? (
        <p className="muted" style={{ margin: 0 }}>No locations.</p>
      ) : (
        <ul className="daylist">
          {rows.map((l) => (
            <li key={l.id} className="daylist__row" style={{ flexWrap: 'wrap' }}>
              <span className="daylist__date grow">
                {l.canonicalName}
                {!l.isActive && <span className="badge badge--none" style={{ marginLeft: 8 }}>Retired</span>}
                <br />
                <span className="daylist__meta">
                  #{l.id} · {l.timezone}
                  {l.isActive && (
                    <>
                      {' · '}
                      {l.homeInstructors} homed · {l.assignedInstructors} assigned ·{' '}
                      {l.patternSlots} standing class{l.patternSlots === 1 ? '' : 'es'}
                    </>
                  )}
                  {l.aliases.length > 0 && ` · Enrollware: ${l.aliases.join(', ')}`}
                </span>
              </span>

              {canWrite && (
                <span className="row" style={{ gap: 6 }}>
                  <button className="btn btn--ghost" onClick={() => setEditing(l.id)}>
                    Edit
                  </button>
                  {l.isActive ? (
                    <button className="btn btn--ghost" onClick={() => openRetire(l.id)}>
                      Retire
                    </button>
                  ) : (
                    <button
                      className="btn btn--ghost"
                      onClick={() =>
                        run(`${l.canonicalName} is active again.`, () =>
                          rosterApi.setLocationActive(l.id, true),
                        )
                      }
                    >
                      Reactivate
                    </button>
                  )}
                </span>
              )}

              {editing === l.id && (
                <div style={{ width: '100%' }}>
                  <LocationForm
                    existing={l}
                    onCancel={() => setEditing(null)}
                    onSave={async (patch) => {
                      const ok = await run(`${l.canonicalName} updated.`, () =>
                        rosterApi.updateLocation(l.id, patch),
                      );
                      if (ok) setEditing(null);
                    }}
                  />
                </div>
              )}

              {retiring === l.id && (
                <div className="notice notice--info" style={{ width: '100%', alignItems: 'flex-start' }}>
                  <span className="notice__icon" aria-hidden="true">?</span>
                  <span className="grow">
                    {blockers === null ? (
                      'Checking who works here…'
                    ) : blockers.length === 0 ? (
                      <>
                        Nobody is pointing at <strong>{l.canonicalName}</strong>. Retiring it
                        keeps its history and stops it being offered.
                        <br />
                        <span className="row" style={{ marginTop: 8 }}>
                          <button
                            className="btn btn--primary"
                            disabled={busy}
                            onClick={async () => {
                              setBusy(true);
                              await run(`${l.canonicalName} retired.`, () =>
                                rosterApi.setLocationActive(l.id, false),
                              );
                              setBusy(false);
                              setRetiring(null);
                            }}
                          >
                            Retire it
                          </button>
                          <button className="btn btn--secondary" onClick={() => setRetiring(null)}>
                            Cancel
                          </button>
                        </span>
                      </>
                    ) : (
                      <>
                        <strong>{l.canonicalName}</strong> cannot be retired yet.
                        {/* §4.2: a standing pattern left pointing at a dead site keeps
                            generating availability the coverage grid counts. */}
                        <ul className="small" style={{ margin: '8px 0', paddingLeft: 18 }}>
                          {blockers.map((b, i) => (
                            <li key={`${b.instructorId}-${b.reason}-${i}`}>
                              {b.name} {BLOCKER_REASON[b.reason]}
                              {b.reason === 'pattern' &&
                                ` (${b.patternSlots} slot${b.patternSlots === 1 ? '' : 's'})`}
                            </li>
                          ))}
                        </ul>
                        Deactivate those instructors first, or move everything to another
                        site:
                        <span className="row" style={{ marginTop: 8, flexWrap: 'wrap' }}>
                          <select
                            className="select"
                            style={{ maxWidth: 260 }}
                            value={moveTo ?? ''}
                            onChange={(e) => setMoveTo(e.target.value ? Number(e.target.value) : null)}
                          >
                            <option value="">Move everything to…</option>
                            {active
                              .filter((other) => other.id !== l.id)
                              .map((other) => (
                                <option key={other.id} value={other.id}>
                                  {other.canonicalName}
                                </option>
                              ))}
                          </select>
                          <button
                            className="btn btn--secondary"
                            disabled={!moveTo || busy}
                            onClick={async () => {
                              if (!moveTo) return;
                              setBusy(true);
                              const target = active.find((a) => a.id === moveTo);
                              const ok = await run(
                                `Moved everything from ${l.canonicalName} to ${target?.canonicalName}.`,
                                () => rosterApi.reassignLocation(l.id, moveTo),
                              );
                              setBusy(false);
                              if (ok) await openRetire(l.id);
                            }}
                          >
                            Move
                          </button>
                          <button className="btn btn--ghost" onClick={() => setRetiring(null)}>
                            Cancel
                          </button>
                        </span>
                      </>
                    )}
                  </span>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/**
 * Add or edit a site.
 *
 * Luke asked for this explicitly (2026-08-23): adding a location is an admin job, and the
 * flow where an instructor tells us about a new site stays as it is. They never create one
 * themselves — a site typed by whoever happened to teach there first is how you end up with
 * "Manhattan, NY" and "Midtown Manhattan, NY" as separate rows, which is the exact mess
 * SPEC §4.2's alias table exists to clean up.
 */
function LocationForm({
  existing,
  onSave,
  onCancel,
}: {
  existing?: RosterLocationRow;
  onSave: (input: LocationInput) => Promise<void>;
  onCancel: () => void;
}) {
  const [canonicalName, setName] = useState(existing?.canonicalName ?? '');
  const [timezone, setTimezone] = useState(existing?.timezone ?? 'America/New_York');
  const [address, setAddress] = useState(existing?.address ?? '');
  const [city, setCity] = useState(existing?.city ?? '');
  const [state, setState] = useState(existing?.state ?? '');
  // One per line, NOT comma-separated: every one of these strings contains a comma
  // ("St. Louis, MO"), so splitting on one would quietly cut them in half.
  const [aliases, setAliases] = useState((existing?.aliases ?? []).join('\n'));
  const [saving, setSaving] = useState(false);

  return (
    <form
      className="stack"
      style={{ marginTop: 12 }}
      onSubmit={async (e) => {
        e.preventDefault();
        setSaving(true);
        await onSave({
          canonicalName,
          timezone,
          address: address.trim() || null,
          city: city.trim() || null,
          state: state.trim() || null,
          aliases: aliases.split('\n').map((a) => a.trim()).filter(Boolean),
        });
        setSaving(false);
      }}
    >
      <div className="row row--wrap" style={{ gap: 'var(--space-2)' }}>
        <label className="field grow" style={{ minWidth: 220 }}>
          <span className="field__label">Name</span>
          <input
            className="input"
            value={canonicalName}
            placeholder="Pearl River, NY"
            onChange={(e) => setName(e.target.value)}
            required
          />
        </label>
        <label className="field" style={{ minWidth: 200 }}>
          <span className="field__label">Time zone</span>
          <select className="select" value={timezone} onChange={(e) => setTimezone(e.target.value)}>
            {TIMEZONES.map((tz) => (
              <option key={tz.value} value={tz.value}>{tz.label}</option>
            ))}
          </select>
          {/* Not cosmetic: the zone decides what counts as a past date for everyone who
              works here (SPEC §3.5), so a wrong one silently moves their deadline. */}
          <span className="small muted">Decides when a day counts as past for this site.</span>
        </label>
      </div>

      <div className="row row--wrap" style={{ gap: 'var(--space-2)' }}>
        <label className="field grow" style={{ minWidth: 220 }}>
          <span className="field__label">Address</span>
          <input className="input" value={address} onChange={(e) => setAddress(e.target.value)} />
        </label>
        <label className="field" style={{ minWidth: 140 }}>
          <span className="field__label">City</span>
          <input className="input" value={city} onChange={(e) => setCity(e.target.value)} />
        </label>
        <label className="field" style={{ minWidth: 100 }}>
          <span className="field__label">State</span>
          <input className="input" value={state} onChange={(e) => setState(e.target.value)} />
        </label>
      </div>

      <label className="field">
        <span className="field__label">Enrollware names for this site</span>
        <textarea
          className="input"
          rows={3}
          value={aliases}
          placeholder={'St. Louis, MO'}
          onChange={(e) => setAliases(e.target.value)}
        />
        {/* §4.2: both live aliases are real divergences, not typos. Without them the class
            report stops matching and the site looks like it has no classes at all. */}
        <span className="small muted">
          One per line. Only needed when Enrollware writes this site under a different name.
        </span>
      </label>

      <div className="row">
        <button className="btn btn--primary" type="submit" disabled={saving}>
          {saving ? 'Saving…' : existing ? 'Save changes' : 'Add location'}
        </button>
        <button className="btn btn--secondary" type="button" onClick={onCancel} disabled={saving}>
          Cancel
        </button>
      </div>
    </form>
  );
}
